// src/TopologyMap.tsx

import React, { useEffect, useRef } from 'react';

declare global {
  interface Window {
    TopoSphere: any;
    topoSphere: any;
  }
}

interface TopologyMapProps {
  topologyData: object;
  config: object;
  onInit?: (instance: any) => void;
}

const TopologyMap: React.FC<TopologyMapProps> = ({ topologyData, config, onInit }) => {
  const isInitialized = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<any>(null);

  useEffect(() => {
    if (isInitialized.current) return;

    const initializeTopology = async () => {
      if (!containerRef.current || !window.TopoSphere) return;

      try {
        isInitialized.current = true;
        const containerId = 'topology-' + Date.now();
        containerRef.current.id = containerId;

        const instance = await window.TopoSphere.create(containerId, topologyData, {
          ...config,
          // Add responsive configuration
          adaptiveScaling: {
            enabled: true,
            minScale: 0.5,
            maxScale: 2,
            scaleToFit: true
          }
        });
        
        instanceRef.current = instance;
        window.topoSphere = instance;

        // Ensure proper sizing and layout
        const handleResize = () => {
          if (instance?.topology) {
            instance.topology.performResize();
            instance.topology.setLayout('layered');
          }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        
        if (onInit) onInit(instance);

        return () => window.removeEventListener('resize', handleResize);
      } catch (error) {
        console.error('Failed to initialize topology:', error);
        isInitialized.current = false;
      }
    };

    initializeTopology();

    return () => {
      if (instanceRef.current?.destroy) {
        instanceRef.current.destroy();
        instanceRef.current = null;
        isInitialized.current = false;
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full h-full bg-[#1a1a1a]"
      style={{ position: 'absolute', inset: 0 }}
    />
  );
};

export default TopologyMap;